import extractErrorMessage from '../utils/extractErrorMessage';
import { authClient } from './client';

// Client pages: the API answers 403 when the client is not in the viewer's network or the
// role lacks the permission. One Serbian message instead of the API's English text.
const NO_CLIENT_ACCESS = 'Nemate pristup podacima ovog korisnika (nije u vašoj mreži ili vaša rola nema dozvolu).';
const clientPageError = (error, fallback) =>
  error.response?.status === 403 ? NO_CLIENT_ACCESS : extractErrorMessage(error, fallback);


export const fetchUser = async (id) => {
  try {
    const response = await authClient.get(`/users/${id}`);
    return response.data;
  } catch (error) {
    const message = clientPageError(
      error,
      'Došlo je do greške pri dobavljanju korisnika.'
    );
    return { data: null, success: false, message: message };
  }
};

export const fetchUsers = async ({
  page = 1,
  limit = 10,
  filter = null,
  search = null,
}) => {
  try {
    let query = `?page=${page}&limit=${limit}`;

    Object.entries(filter).forEach(([key, value]) => {
      if (!value || value === 'all') return;

      if (Array.isArray(value)) {
        query += `&${key}=${value.map((v) => encodeURIComponent(v)).join(',')}`;
      } else {
        query += `&${key}=${encodeURIComponent(value)}`;
      }
    });

    if (search) {
      query += `&search=${encodeURIComponent(search)}`;
    }

    const response = await authClient.get(`/users${query}`);

    return response.data;
    // return {
    //   data: {
    //     users: response.data.users,
    //     page: response.data.page,
    //     totalPages: response.data.totalPages,
    //   },
    //   error: null,
    // };
  } catch (error) {
    const message = extractErrorMessage(
      error,
      'Došlo je do greške pri dobavljanju korisnika.'
    );
    return { data: null, success: false, message: message };
  }
};

export const fetchUserCount = async () => {
  try {
    const response = await authClient.get(`/users-count`);
    return { data: response.data.count, error: null };
  } catch (error) {
    const message = extractErrorMessage(
      error,
      'Došlo je do greške pri dobavljanju broja korisnika.'
    );
    return { data: null, success: false, message: message };
    // return { data: null, error: message };
  }
};

export const deleteUser = async (id) => {
  try {
    const response = await authClient.delete(`/users/${id}/delete`);
    return response.data;
    // return { data: response.data, error: null };
  } catch (error) {
    const message = extractErrorMessage(
      error,
      'Došlo je do greške pri brisanju korisnika.'
    );
    return { data: null, success: false, message: message };
    // return { data: null, error: message };
  }
};

export const verifyUser = async (
  id,
  isDocumentVerified,
  reasonForRejection = ''
) => {
  try {
    const response = await authClient.patch(`/user-account/verify/${id}`, {
      documentVerified: isDocumentVerified,
      reasonForRejection: reasonForRejection,
    });

    return { data: response.data, error: null };
  } catch (error) {
    const message = extractErrorMessage(
      error,
      'Došlo je do greške pri verifikaciji korisnika.'
    );
    return { data: null, success: false, message: message };
    // return { data: null, error: message };
  }
};

export const changeVerificationStatus = async (id, isVerified, type) => {
  const status =
    isVerified === true
      ? 'active'
      : type === 'company'
        ? 'pending_admin_verification'
        : 'pending_verification';

  try {
    const response = await authClient.patch(`/user/${id}`, {
      status,
      documentVerified: isVerified,
    });

    return { data: response.data, error: null };
  } catch (error) {
    const message = extractErrorMessage(
      error,
      'Došlo je do greške pri arhiviranju korisnika.'
    );
    return { data: null, success: false, message: message };
    // return { data: null, error: message };
  }
};

export const archiveUser = async (id) => {
  try {
    const response = await authClient.patch(`/user-archive/${id}`);

    return { data: response.data, error: null };
  } catch (error) {
    const message = extractErrorMessage(
      error,
      'Došlo je do greške pri arhiviranju korisnika.'
    );
    return { data: null, success: false, message: message };
    // return { data: null, error: message };
  }
};

export const blockUser = async (id) => {
  try {
    const response = await authClient.put(`/users/${id}/block`);
    return response.data;
  } catch (error) {
    const message = extractErrorMessage(
      error,
      'Došlo je do greške pri blokiranju korisnika.'
    );
    return { data: null, success: false, message: message };
    // return { data: null, error: message };
  }
};

export const unblockUser = async (id) => {
  try {
    const response = await authClient.put(`/users/${id}/unblock`);
    return response.data;
  } catch (error) {
    const message = extractErrorMessage(
      error,
      'Došlo je do greške pri odblokiranju korisnika.'
    );
    return { data: null, success: false, message: message };
  }
};

// Permanent delete: the backend archives the client (history kept, email freed for a new
// account). Unlike deleteUser (soft, INACTIVE), it cannot be undone from "Na čekanju".
export const hardDeleteUser = async (id) => {
  try {
    const response = await authClient.delete(`/users/${id}/hard-delete`);
    return response.data;
  } catch (error) {
    const message = extractErrorMessage(
      error,
      'Došlo je do greške pri trajnom brisanju korisnika.'
    );
    return { data: null, success: false, message: message };
  }
};

export const reactivateUser = async (id) => {
  try {
    const response = await authClient.put(`/users/${id}/reactivate`);
    return response.data;
  } catch (error) {
    const message = extractErrorMessage(
      error,
      'Došlo je do greške pri reaktivaciji korisnika.'
    );
    return { data: null, success: false, message: message };
  }
};

export const verifyPendingUser = async (id) => {
  try {
    const response = await authClient.put(`/users/${id}/verify`);
    return response.data;
  } catch (error) {
    const message = extractErrorMessage(
      error,
      'Došlo je do greške pri verifikaciji korisnika.'
    );
    return { data: null, success: false, message: message };
  }
};

export const updateUser = async (id, formData, user) => {
  try {
    const updatedFields = {};
    Object.keys(formData).forEach((key) => {
      if (
        key === 'password'
          ? formData.password &&
            formData.password !== (user?.passwordToken || '')
          : formData[key] !== (user?.[key] || '')
      ) {
        updatedFields[key] = formData[key];
      }
    });

    const response = await authClient.patch(`/user/${id}`, updatedFields);

    return {
      user: response.data.user,
      message: response.data.message || 'Korisnik uspešno ažuriran.',
      error: null,
    };
  } catch (error) {
    const message = extractErrorMessage(
      error,
      'Došlo je do greške pri ažuriranju korisnika.'
    );
    return { user: null, message: null, error: message };
  }
};

export const editUserProfile = async ({ id, payload }) => {
  try {
    const response = await authClient.put(`/users/${id}/profile`, payload);

    return {
      data: response.data,
      success: true,
      message:
        response.data.message || 'Podaci o korisniku su uspešno ažurirani.',
    };
  } catch (error) {
    const message = extractErrorMessage(
      error,
      'Došlo je do greške pri ažuriranju podataka o korisniku.'
    );
    return { data: null, success: false, message: message };
  }
};

export const editDoctor = async ({ id, payload }) => {
  try {
    const response = await authClient.patch(`/doctors/${id}`, payload);

    return {
      data: response.data,
      success: true,
      message:
        response.data.message || 'Podaci o mentoru su uspešno ažurirani.',
    };
  } catch (error) {
    const message = extractErrorMessage(
      error,
      'Došlo je do greške pri ažuriranju podataka o mentoru.'
    );
    return { data: null, success: false, message: message };
  }
};

export const editClinic = async ({ id, payload }) => {
  try {
    const response = await authClient.patch(`/clinics/${id}`, payload);

    return {
      data: response.data,
      success: true,
      message:
        response.data.message || 'Podaci o klinici su uspešno ažurirani.',
    };
  } catch (error) {
    const message = extractErrorMessage(
      error,
      'Došlo je do greške pri ažuriranju podataka o klinici.'
    );
    return { data: null, success: false, message: message };
  }
};

export const saveCompanyAddress = async (user, municipality, city, address) => {
  try {
    // if (user.type !== 'company') {
    //   return {
    //     user: null,
    //     message: null,
    //     error: 'Korisnik nije pravno lice.',
    //   };
    // }

    if (!address || !municipality || !city) {
      return {
        user: null,
        message: null,
        error: 'Nisu uneti ispravni podaci.',
      };
    }

    const existingCompanyAddresses = user.companyAddresses;
    const newCompanyAddress = { municipality, city, address };
    const updatedData = {
      companyAddresses: existingCompanyAddresses
        ? [...existingCompanyAddresses, newCompanyAddress]
        : [newCompanyAddress],
    };

    const response = await authClient.patch(`/user/${user._id}`, updatedData);

    return {
      updatedUser: response.data.user,
      message: response.data.message || 'Adresa uspešno dodata.',
      error: null,
    };
  } catch (error) {
    const message = extractErrorMessage(
      error,
      'Došlo je do greške pri dodavanju adrese kompanije korisnika.'
    );
    return { user: null, message: null, error: message };
  }
};

export const updateOwnAccount = async (formData, user) => {
  try {
    const updatedFields = {};

    Object.keys(formData).forEach((key) => {
      if (
        key === 'password'
          ? formData.password &&
            formData.password !== (user?.passwordToken || '')
          : JSON.stringify(formData[key]) !== JSON.stringify(user?.[key])
      ) {
        updatedFields[key] = formData[key];
      }
    });

    const response = await authClient.patch(
      `/user-account/${user._id}`,
      updatedFields
    );

    return {
      user: response.data.user,
      message: response.data.message || 'Nalog je uspešno ažuriran.',
      error: null,
    };
  } catch (error) {
    const message = extractErrorMessage(
      error,
      'Došlo je do greške pri ažuriranju naloga.'
    );
    return { user: null, message: null, error: message };
  }
};

export const createUser = async (userData) => {
  try {
    let route;
    if (userData.type === 'individual') {
      route = '/auth/register';
      // route = '/register-admin';
    } else {
      route = '/auth/register';
      // route = '/register-company-admin';
    }

    const response = await authClient.post(route, userData);

    return response.data;
  } catch (error) {
    const message = extractErrorMessage(
      error,
      'Došlo je do greške pri kreaciji novog korisnika.'
    );
    return { data: null, success: false, message: message };
  }
};

export const createAdmin = async (adminData) => {
  try {
    const response = await authClient.post('/users', {
      ...adminData,
      role: 'admin',
    });

    return response.data;
  } catch (error) {
    const message = extractErrorMessage(
      error,
      'Došlo je do greške pri kreiranju admina.'
    );
    return { data: null, success: false, message: message };
  }
};

export const exportUsers = async ({
  role = null,
  search = null,
  vipStatus = null,
  status = null,
}) => {
  try {
    const params = new URLSearchParams();

    if (role) params.append('role', role);
    if (search) params.append('search', search);
    if (vipStatus) params.append('vipStatus', vipStatus);
    if (status) params.append('status', status);

    const query = params.toString() ? `?${params.toString()}` : '';

    const response = await authClient.get(`/users/export${query}`, {
      responseType: 'blob',
    });

    if (response.headers['content-type']?.includes('application/json')) {
      const text = await response.data.text();
      const jsonData = JSON.parse(text);
      return {
        data: null,
        success: jsonData.success,
        message: jsonData.message,
      };
    }

    const url = window.URL.createObjectURL(new Blob([response.data]));
    const link = document.createElement('a');
    link.href = url;
    link.setAttribute(
      'download',
      `korisnici-${role ?? 'svi'}-${new Date().toISOString().split('T')[0]}.csv`
    );
    document.body.appendChild(link);
    link.click();
    link.remove();
    window.URL.revokeObjectURL(url);

    return {
      data: true,
      success: true,
      message: 'Korisnici uspešno izvezeni',
    };
  } catch (error) {
    const message = extractErrorMessage(
      error,
      'Došlo je do greške pri izvozu korisnika.'
    );
    return { data: null, success: false, message: message };
  }
};

export const applyReferralCode = async (code) => {
  try {
    const response = await authClient.post(`/users/apply-referral`, { code });
    return response.data;
  } catch (error) {
    const message = extractErrorMessage(
      error,
      'Došlo je do greške pri primeni referral koda.'
    );
    return { data: null, success: false, message };
  }
};

export const removeReferredByDoctor = async (userId) => {
  try {
    const response = await authClient.delete(
      `/users/${userId}/referred-by-doctor`
    );
    return response.data;
  } catch (error) {
    const message = extractErrorMessage(
      error,
      'Greška pri uklanjanju mentorske preporuke.'
    );
    return { data: null, success: false, message };
  }
};

export const setReferredByDoctor = async (userId, doctorId) => {
  try {
    const response = await authClient.put(
      `/users/${userId}/referred-by-doctor`,
      { doctorId }
    );
    return response.data;
  } catch (error) {
    const message = extractErrorMessage(
      error,
      'Greška pri postavljanju mentorske preporuke.'
    );
    return { data: null, success: false, message };
  }
};

export const fetchUserActivity = async (id) => {
  try {
    const response = await authClient.get(`/users/${id}/activity`);
    return response.data;
  } catch (error) {
    const message = clientPageError(
      error,
      'Došlo je do greške pri dobavljanju aktivnosti.'
    );
    return { data: null, success: false, message: message };
  }
};

export const fetchUsersPersonalSurvey = async (id) => {
  try {
    const response = await authClient.get(`/users/${id}/personal-survey`);
    return response.data;
  } catch (error) {
    const message = clientPageError(
      error,
      'Došlo je do greške pri dobavljanju korisnika.'
    );
    return { data: null, success: false, message: message };
  }
};

export const updatePersonalSurveyPartA = async ({ id, payload }) => {
  try {
    const response = await authClient.post(
      `/users/${id}/personal-survey/part-a`,
      payload
    );
    return {
      data: response.data.data,
      success: true,
      message: response.data.message || 'Anketa uspešno ažurirana.',
    };
  } catch (error) {
    const message = extractErrorMessage(
      error,
      'Došlo je do greške pri ažuriranju ankete.'
    );
    return { data: null, success: false, message };
  }
};

export const submitPersonalSurveyHealthConsent = async (id) => {
  try {
    const response = await authClient.post(
      `/users/${id}/personal-survey/health-consent`,
      { consent: true }
    );
    return {
      data: response.data.data,
      success: true,
      message: response.data.message || 'Saglasnost zabeležena.',
    };
  } catch (error) {
    const message = extractErrorMessage(
      error,
      'Došlo je do greške pri beleženju saglasnosti.'
    );
    return { data: null, success: false, message };
  }
};

export const updatePersonalSurveyModule1 = async ({ id, payload }) => {
  try {
    const response = await authClient.post(
      `/users/${id}/personal-survey/module-1`,
      payload
    );
    return {
      data: response.data.data,
      success: true,
      message: response.data.message || 'Modul uspešno ažuriran.',
    };
  } catch (error) {
    const message = extractErrorMessage(
      error,
      'Došlo je do greške pri ažuriranju modula.'
    );
    return { data: null, success: false, message };
  }
};

export const updatePersonalSurveyModule2 = async ({ id, payload }) => {
  try {
    const response = await authClient.post(
      `/users/${id}/personal-survey/module-2`,
      payload
    );
    return {
      data: response.data.data,
      success: true,
      message: response.data.message || 'Modul uspešno ažuriran.',
    };
  } catch (error) {
    const message = extractErrorMessage(
      error,
      'Došlo je do greške pri ažuriranju modula.'
    );
    return { data: null, success: false, message };
  }
};

export const updatePersonalSurveyModule3 = async ({ id, payload }) => {
  try {
    const response = await authClient.post(
      `/users/${id}/personal-survey/module-3`,
      payload
    );
    return {
      data: response.data.data,
      success: true,
      message: response.data.message || 'Modul uspešno ažuriran.',
    };
  } catch (error) {
    const message = extractErrorMessage(
      error,
      'Došlo je do greške pri ažuriranju modula.'
    );
    return { data: null, success: false, message };
  }
};

export const updatePersonalSurveyModule4 = async ({ id, payload }) => {
  try {
    const response = await authClient.post(
      `/users/${id}/personal-survey/module-4`,
      payload
    );
    return {
      data: response.data.data,
      success: true,
      message: response.data.message || 'Modul uspešno ažuriran.',
    };
  } catch (error) {
    const message = extractErrorMessage(
      error,
      'Došlo je do greške pri ažuriranju modula.'
    );
    return { data: null, success: false, message };
  }
};

export const updatePersonalSurveyModule5 = async ({ id, payload }) => {
  try {
    const response = await authClient.post(
      `/users/${id}/personal-survey/module-5`,
      payload
    );
    return {
      data: response.data.data,
      success: true,
      message: response.data.message || 'Modul uspešno ažuriran.',
    };
  } catch (error) {
    const message = extractErrorMessage(
      error,
      'Došlo je do greške pri ažuriranju modula.'
    );
    return { data: null, success: false, message };
  }
};

export const updatePersonalSurveyModule6 = async ({ id, payload }) => {
  try {
    const response = await authClient.post(
      `/users/${id}/personal-survey/module-6`,
      payload
    );
    return {
      data: response.data.data,
      success: true,
      message: response.data.message || 'Modul uspešno ažuriran.',
    };
  } catch (error) {
    const message = extractErrorMessage(
      error,
      'Došlo je do greške pri ažuriranju modula.'
    );
    return { data: null, success: false, message };
  }
};

export const updatePersonalSurveyModule7 = async ({ id, payload }) => {
  try {
    const response = await authClient.post(
      `/users/${id}/personal-survey/module-7`,
      payload
    );
    return {
      data: response.data.data,
      success: true,
      message: response.data.message || 'Modul uspešno ažuriran.',
    };
  } catch (error) {
    const message = extractErrorMessage(
      error,
      'Došlo je do greške pri ažuriranju modula.'
    );
    return { data: null, success: false, message };
  }
};

export const updatePersonalSurveyModule8 = async ({ id, payload }) => {
  try {
    const response = await authClient.post(
      `/users/${id}/personal-survey/module-8`,
      payload
    );
    return {
      data: response.data.data,
      success: true,
      message: response.data.message || 'Modul uspešno ažuriran.',
    };
  } catch (error) {
    const message = extractErrorMessage(
      error,
      'Došlo je do greške pri ažuriranju modula.'
    );
    return { data: null, success: false, message };
  }
};

export const exportPersonalSurvey = async (id) => {
  try {
    const response = await authClient.get(
      `/users/${id}/personal-survey/export`,
      {
        responseType: 'blob',
      }
    );

    if (response.headers['content-type']?.includes('application/json')) {
      const text = await response.data.text();
      const jsonData = JSON.parse(text);
      return {
        data: null,
        success: jsonData.success,
        message: jsonData.message,
      };
    }

    const url = window.URL.createObjectURL(new Blob([response.data]));
    const link = document.createElement('a');
    link.href = url;
    link.setAttribute(
      'download',
      `anketa-${id}-${new Date().toISOString().split('T')[0]}.csv`
    );
    document.body.appendChild(link);
    link.click();
    link.remove();
    window.URL.revokeObjectURL(url);

    return {
      data: true,
      success: true,
      message: 'Anketa uspešno izvezena',
    };
  } catch (error) {
    const message = extractErrorMessage(
      error,
      'Došlo je do greške pri izvozu ankete.'
    );
    return { data: null, success: false, message };
  }
};
